import {useIntl} from 'react-intl';
import {Table, Tbody, Td, Tr, useBoolean, Box, Center} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';

import type {Player} from '@eksab/features/competition/types';
import {ChevronIcon} from '@eksab/features/competition/ranking/icons';

const MAX_COLLAPSED_STATS = 3;

type Stat = keyof Player['stats'];

interface StatsTableProps {
  stats: {
    name: Stat;
    value: number;
  }[];
}

export const StatsTable = ({stats}: StatsTableProps) => {
  const format = useIntl().formatNumber;
  const [statsExpanded, {toggle}] = useBoolean(false);

  const t = useTranslate();

  const shownStats = statsExpanded ? stats.length : Math.min(stats.length, MAX_COLLAPSED_STATS);

  return (
    <>
      <Box h={`${shownStats * 1.75}rem`} transition="height 0.5s ease-out" overflow="hidden">
        <Table variant="striped">
          <Tbody>
            {stats.map(({name, value}) => (
              <Tr key={name} h="1.75rem" sx={{td: {border: 0}}}>
                <Td h="full" ps="2" pe="0" py="1" fontSize="sm" textTransform="capitalize" color="blackAlpha.700">
                  {t(`fantasy.lineup.stats.${name as Stat}`)}
                </Td>

                <Td
                  h="full"
                  ps="0"
                  pe="2"
                  py="1"
                  textAlign="end"
                  fontWeight="700"
                  fontSize="sm"
                  sx={{fontVariantNumeric: 'tabular-nums'}}
                >
                  {format(value)}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {stats.length > MAX_COLLAPSED_STATS && (
        <Center
          gap="1.5"
          color="green.400"
          bgColor="rgba(90, 197, 123, 0.1)"
          fontSize="xs"
          fontWeight="700"
          px="2"
          py="1"
          rounded="sm"
          cursor="pointer"
          onClick={toggle}
        >
          {t(`app.actions.${statsExpanded ? 'less' : 'more'}`)}
          <ChevronIcon
            mt="0.5"
            width="2"
            transform="auto"
            scaleY={statsExpanded ? 1 : -1}
            transition="transform 0.5s"
          />
        </Center>
      )}
    </>
  );
};
